import { Component } from '@theme/component'
import { trapFocus, removeTrapFocus } from '@theme/focus'
import { onAnimationEnd } from '@theme/utilities'

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details']

  connectedCallback() {
    super.connectedCallback()

    this.addEventListener('keyup', this.#onKeyUp)
    this.#setupAnimatedElementListeners()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('keyup', this.#onKeyUp)
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return

    this.#close(this.#getDetailsElement(event))
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open')
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details

    return event.target.closest('details') ?? this.refs.details
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open()
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event)
    const summary = details.querySelector('summary')

    if (!summary) return

    summary.setAttribute('aria-expanded', 'true')

    this.preventInitialAccordionAnimations(details)
    requestAnimationFrame(() => {
      details.classList.add('menu-open')
      const drawer = this.querySelector('[data-header-drawer]')
      if (
        drawer &&
        details !== this.refs.details &&
        details.querySelector('.menu-drawer__submenu')
      ) {
        // phase 1: show panel
        drawer.classList.add('has-open-submenu')

        // phase 2: stagger items AFTER panel starts animating
        drawer.classList.remove('submenu-items-in')

        // Wait a tick so the browser applies has-open-submenu styles first
        requestAnimationFrame(() => {
          // Option A: start item stagger shortly after fade begins (snappier)
          setTimeout(() => drawer.classList.add('submenu-items-in'), 120)

          // Option B (more strict): start after full panel duration
          // setTimeout(() => drawer.classList.add('submenu-items-in'), 320)
        })
      }
      setTimeout(() => {
        trapFocus(details)
      }, 0)
    })
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event))
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details)
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary')

    if (!summary) return

    const drawer = this.querySelector('[data-header-drawer]')
    const isSubmenu =
      drawer && details !== this.refs.details && details.querySelector('.menu-drawer__submenu')

    if (isSubmenu) {
      drawer.classList.add('is-submenu-closing')
      drawer.classList.remove('submenu-items-in')
    }

    const doClose = () => {
      summary.setAttribute('aria-expanded', 'false')
      details.classList.remove('menu-open')
      if (isSubmenu) {
        drawer.classList.remove('has-open-submenu')
      }

      onAnimationEnd(details, () => {
        reset(details)
        if (details === this.refs.details) {
          removeTrapFocus()
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)')
          openDetails.forEach(reset)
          if (drawer) {
            drawer.classList.remove('has-open-submenu')
            drawer.classList.remove('is-submenu-closing')
          }
        } else {
          setTimeout(() => {
            trapFocus(this.refs.details)
          }, 0)
          if (drawer) {
            const panel = details.querySelector('.menu-drawer__submenu')
            const closeDuration = panel ? 500 : 0
            setTimeout(() => drawer.classList.remove('is-submenu-closing'), closeDuration)
          }
        }
      })
    }

    if (isSubmenu) {
      setTimeout(doClose, 160)
    } else {
      doClose()
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset')
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd)
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element')
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd)
    })
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content')

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation')
      }
    })
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation')
        }
      })
    }, 100)
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer)
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open')
  element.removeAttribute('open')
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false')

  const drawer = element.querySelector('[data-header-drawer]')
  drawer?.classList?.remove('submenu-items-in')
}
